/**
 * Copy de la página de Multiubicaciones (`/v3/multiubicaciones`).
 *
 * Es la página comercial del producto: la que lee un grupo o una franquicia
 * antes de rellenar el formulario. Por eso es la que más afirmaciones concretas
 * hace de todo el rediseño, y la que más se ha contrastado contra el código.
 *
 * El copy del formulario NO está aquí: vive en `landingV3.content.js` bajo
 * `contacto`, porque el componente es uno solo y mañana puede montarlo otra
 * página.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * DOS AFIRMACIONES DEL DISEÑO QUE NO SE COPIAN
 * ────────────────────────────────────────────────────────────────────────────
 *
 * 1. **«Las bajas nunca se contestan sin ti»: FALSO**, y además se contradice
 *    con la página de Automatizaciones de este mismo rediseño, que presume —con
 *    razón— de que el auto-responder llega «de 5 estrellas a 1, sin
 *    restricciones» (`MAX_REPLY_RATING_VALUE = 5`). Dejar las bajas en manos de
 *    una persona es el valor por DEFECTO (`DEFAULT_REPLY_RATINGS = [4, 5]`) y
 *    lo recomendable, no una barrera del producto. Se cuenta como lo que es.
 *
 * 2. **El Mapa SEO de grupo no es de «toda la ciudad».** El diseño lo vende
 *    como «la fuerza real de tu marca sumando todos tus locales a la vez... en
 *    toda la ciudad». Lo que hace `compute_group_metrics` es otra cosa, y más
 *    modesta: sobre la rejilla YA escaneada de un local, toma en cada punto el
 *    mejor puesto entre el tuyo y el de cualquier hermano que aparezca ahí. Es
 *    por escaneo, no una unión de zonas. Y es CONSERVADOR a propósito: el
 *    puesto de un hermano solo puede salir del top-20 que se guarda por punto,
 *    así que un local tuyo que rankee 25 cuenta como que no aparece. La propia
 *    interfaz lo avisa (`groupDepthNote`), y aquí no se promete de más.
 *
 * Todo lo demás se comprobó y encaja: el selector de local, la bandeja única
 * (`location_ids` con «todos / uno / varios»), los permisos por sede
 * (`collaborators` guarda `location_ids` por colaborador), la posición media,
 * cobertura y dominancia, el histórico, la puntuación y las seis áreas por
 * local, el tono propio o de marca, las palabras clave compartidas, las
 * publicaciones con variables a varias sedes, y el informe mensual: existe la
 * variante agregada de marca además de la individual (`VARIANTES = ('monthly',
 * 'aggregated')`, con su «Informe de marca · {período}»).
 */

/**
 * Las capturas del panel, ya optimizadas a WebP (2,2 MB de PNG -> 500 KB).
 *
 * Cada una lleva su tamaño real: con `width` y `height` en el `<img>`, el
 * navegador reserva el hueco antes de descargarla y la página no pega el salto
 * al cargar. Son proporciones muy distintas —de 2,43 a 0,69— así que ninguna
 * se fuerza a una común.
 *
 * La del Mapa SEO sale DOS veces, en el hero y en «Escáner por local»: es la
 * pantalla completa, y las dos secciones hablan de ella desde ángulos
 * distintos.
 */
const RUTA = '/landing-images/v3/multiubicaciones';

/*
 * Solo la GEOMETRÍA. El `alt` no está aquí porque sí se traduce, y se pone en
 * cada idioma con `{ ...MAPA, alt: '…' }`.
 *
 * Partirlo así no es cosmética: si cada idioma repitiera el `src`, el ancho y
 * el alto, bastaría con recortar una captura y olvidarse de cuatro sitios para
 * que la página pegue el salto de carga en francés y no en español. Aquí eso
 * no se puede escribir.
 */
const MAPA = { src: `${RUTA}/mapa-seo-grupo.webp`, ancho: 1224, alto: 828 };
const BANDEJA = { src: `${RUTA}/bandeja-resenas.webp`, ancho: 1019, alto: 544 };
const RANKING = { src: `${RUTA}/ranking-locales.webp`, ancho: 723, alto: 298 };
const CUADRICULA = { src: `${RUTA}/cuadricula-detalle.webp`, ancho: 1002, alto: 940 };
const TONO = { src: `${RUTA}/tono-por-locales.webp`, ancho: 577, alto: 832 };
const SUGERENCIAS = { src: `${RUTA}/publicacion-sugerencias-ia.webp`, ancho: 668, alto: 713 };
const INFORME = { src: `${RUTA}/informe-mensual.webp`, ancho: 1412, alto: 890 };

const es = {
  hero: {
    badge: 'Para marcas y grupos con varios locales',
    titulo: 'Que cada local, sin excepción, sea el ',
    acento: 'Nº1 de su zona',
    sub: 'Un panel para controlar todas tus fichas, comparar el rendimiento entre locales y aplicar cambios a todo tu grupo con un clic.',
    cta: 'Solicitar más información',
    pieCaptura: 'El escáner de posiciones, tal cual lo ves dentro del panel',
    captura: {
      ...MAPA,
      alt: 'El Mapa SEO del panel: a la izquierda el formulario de escaneo con el local elegido y los resultados, a la derecha la cuadrícula de posiciones sobre el mapa de la ciudad.',
    },
  },

  problema: {
    titulo: 'Diez locales, diez pestañas abiertas, y ninguna visión de conjunto',
    p1: 'Cada local tiene su propia ficha, sus propias reseñas, su propia posición en Google Maps. Sin un panel que los junte, gestionarlos significa entrar uno por uno, repetir la misma tarea multiplicada por cada sede, y confiar en que nadie se olvide de ninguna.',
    p2: 'Y lo que de verdad se pierde no es solo tiempo. Es la visión de conjunto: mientras estás pendiente del local que más ruido hace, otro puede llevar semanas cayendo posiciones sin que nadie se entere, porque nadie tiene forma de comparar todos a la vez.',
    p3: 'Con dos locales todavía se sostiene a mano. Con cinco ya cuesta, y a partir de diez la gestión deja de ser una tarea y se convierte en un trabajo en sí mismo: alguien de tu equipo dedicando horas a repetir lo mismo con veinte contraseñas distintas, sin que nadie pueda decir con datos qué sede va bien y cuál no.',
  },

  horas: {
    cifra: 'Cientos de horas',
    texto: 'que tu equipo deja de perder al año en tareas repetidas, local a local',
  },

  /**
   * Las seis secciones de función. Todas con la misma forma —kicker, titular,
   * dos párrafos, tres bullets y la caja de «Lo que te ahorra»— porque en el
   * diseño lo son, y el componente las recorre.
   */
  ahorraEtiqueta: 'Lo que te ahorra',
  secciones: [
    {
      clave: 'panel',
      captura: {
        ...BANDEJA,
        alt: 'La bandeja única de reseñas: la lista de todos los locales a la izquierda y la reseña abierta a la derecha, con su respuesta.',
      },
      kicker: 'Panel centralizado',
      titulo: 'Todos tus locales, una sola pantalla',
      p1: 'Cada ficha de Google que gestionas vive en el mismo panel, sin entrar y salir de cuenta en cuenta. Eliges el local desde un selector y todo lo que ves debajo es de esa sede: sus reseñas, su posición, su puntuación.',
      p2: 'Y las reseñas de todos tus locales llegan a una bandeja única, así que responder no es saltar de ficha en ficha, es una sola lista que vas vaciando hasta el final.',
      bullets: [
        {
          clave: 'acceso', t: 'Un solo acceso',
          d: 'Sin repartir contraseñas de Google entre responsables de sede.',
        },
        {
          clave: 'bandeja', t: 'Bandeja unificada',
          d: 'Cada reseña llega con el nombre de su local al lado, así sabes de dónde viene sin cambiar de pantalla.',
        },
        {
          clave: 'permisos', t: 'Permisos por sede',
          d: 'Cada responsable ve lo suyo, y tú ves todo.',
        },
      ],
      ahorra: 'Una sesión al día en vez de una por local.',
    },
    {
      clave: 'comparar',
      captura: {
        ...RANKING,
        alt: 'El ranking entre locales: cuatro tarjetas con la puntuación SEO de cada uno y su nivel, ordenadas de mejor a peor.',
      },
      kicker: 'Comparar y priorizar',
      titulo: 'Sabes exactamente cuál necesita ayuda hoy',
      p1: 'Ves la posición media de cada local, uno al lado del otro, sin entrar a mirarlos por separado. Mientras uno va bien, otro puede estar cayendo, y aquí es donde se nota antes de que sea tarde.',
      p2: 'Sabes por cuál empezar, en vez de adivinar o repasar local a local a ver si algo falla. Y como el histórico se guarda, no solo ves la foto de hoy: ves quién sube y quién baja mes a mes.',
      bullets: [
        {
          clave: 'media', t: 'Posición media por sede',
          d: 'El puesto medio de cada local contando solo los puntos donde aparece.',
        },
        {
          clave: 'cobertura', t: 'Cobertura y dominancia',
          d: 'En cuántos puntos sale cada uno, y en cuántos está entre los tres primeros.',
        },
        {
          clave: 'historico', t: 'Histórico mes a mes',
          d: 'La tendencia de cada sede, para distinguir un mal día de una caída real.',
        },
      ],
      ahorra: 'Deja de revisar diez fichas para encontrar la que falla.',
    },
    {
      clave: 'escaner',
      // La misma que el hero: es la pantalla completa, y las dos
      // secciones hablan de ella desde ángulos distintos.
      captura: {
        ...MAPA,
        alt: 'El Mapa SEO del panel: a la izquierda el formulario de escaneo con el local elegido y los resultados, a la derecha la cuadrícula de posiciones sobre el mapa de la ciudad.',
      },
      kicker: 'Escáner por local',
      titulo: 'Cada local, con su propio escáner y su propia puntuación',
      p1: 'Cada sede escanea su zona por separado, con la búsqueda que de verdad usan sus clientes y el tamaño de cuadrícula que tenga sentido para su barrio. Lo que ves de cada una es lo suyo: nada se mezcla ni se diluye entre sedes.',
      p2: 'Y cada local tiene también su propia puntuación SEO de 0 a 100, con su nivel y su próxima acción recomendada, exactamente igual que si gestionaras un solo local.',
      bullets: [
        {
          clave: 'nivel', t: 'Puntuación y nivel propios',
          d: 'De «Empezando» a «Líder local», calculado con los datos reales de esa ficha.',
        },
        {
          clave: 'areas', t: 'Seis áreas medidas',
          d: 'Reputación, actividad, fotos, categorías, keywords y datos de la ficha.',
        },
        {
          clave: 'accion', t: 'Próxima acción por sede',
          d: 'Lo que más le sube a ese local concreto, no un consejo genérico para todo el grupo.',
        },
      ],
      ahorra: 'El diagnóstico de cada sede, hecho y ordenado por impacto.',
    },
    {
      clave: 'grupo',
      captura: {
        ...CUADRICULA,
        alt: 'La cuadrícula de posiciones en detalle sobre el mapa: un disco por punto con el puesto en que sale el negocio, del verde al rojo.',
      },
      kicker: 'Mapa SEO de tu grupo',
      insignia: 'Nuevo',
      titulo: 'Ves cómo cubre tu marca una zona, no solo cómo va un local',
      // Reescrito: el diseño hablaba de «toda la ciudad» y de «sumar todos tus
      // locales». Es por escaneo. Ver la cabecera del fichero.
      p1: 'Además del mapa de cada sede, en cada escaneo puedes ver la cobertura de tu MARCA en esa zona: en cada punto se toma el mejor puesto entre el del local que escaneaste y el de cualquier otro local tuyo que aparezca ahí. Así ves si una esquina la cubre tu otra sede en vez de darla por perdida.',
      p2: 'Es la diferencia entre saber que «el local de la calle Mayor va bien» y saber qué partes de esa zona tienes cubiertas con todo lo que tienes abierto alrededor.',
      bullets: [
        {
          clave: 'dominancia', t: 'Dominancia de marca',
          d: 'En qué porcentaje de los puntos de esa zona tu grupo está entre los tres primeros, no solo el local que escaneaste.',
        },
        {
          clave: 'huecos', t: 'Huecos de cobertura',
          d: 'Los puntos donde no llega ninguno de tus locales, que es donde tiene sentido crecer.',
        },
        {
          clave: 'detalle', t: 'Sin perder el detalle',
          d: 'Sigues teniendo el mapa punto por punto de cada sede cuando quieras bajar al caso concreto.',
        },
      ],
      ahorra: 'Un número de cobertura que puedes llevar a dirección sin calcularlo tú.',
      // El aviso que la propia interfaz da (`groupDepthNote`). Va en la página
      // porque promete menos de lo que parece, y es mejor decirlo antes de que
      // alguien lo descubra pagando.
      nota: 'La cuenta es conservadora: el puesto de tus otros locales se lee del top 20 que se guarda de cada punto, así que uno que quede más abajo cuenta como que no aparece. Nunca infla el dato.',
    },
    {
      clave: 'automatizaciones',
      captura: {
        ...TONO,
        alt: 'El diálogo de crear tono: se elige si aplica a las reseñas positivas o críticas y a qué locales, con la opción de todos marcada.',
      },
      kicker: 'Automatizaciones a escala',
      titulo: 'Configúralo una vez, y funciona en todos tus locales',
      // El diseño cerraba con «y las bajas nunca se contestan sin ti», que no
      // es cierto y contradice a la página de Automatizaciones.
      p1: 'Eliges si cada local contesta con su propio tono o si todos hablan con el de la marca. Configuras la regla una vez y se aplica en cada sede, sin repetir el trabajo local por local. Y decides qué puntuaciones se responden solas y con cuánto retardo: de serie se automatizan las de 4 y 5 estrellas y las bajas se quedan en aviso, que es lo sensato, pero puedes automatizar hasta la de 1 estrella si lo prefieres.',
      p2: 'Las palabras que te importan se vigilan en todo el grupo a la vez: en cuanto aparecen en una reseña nueva de cualquiera de tus locales, te llega el email al instante. Y con las alertas por valoración baja, te enteras de la reseña mala en minutos, sea del local que sea.',
      bullets: [
        {
          clave: 'tono', t: 'Tono propio o de marca',
          d: 'Cada sede con su voz, o una sola voz en todos si prefieres coherencia.',
        },
        {
          clave: 'palabras', t: 'Palabras clave compartidas',
          d: 'Una regla general que vigila las reseñas de todos tus locales, y las excepciones que quieras por sede.',
        },
        {
          clave: 'alertas', t: 'Alertas inmediatas',
          d: 'Reaccionas rápido esté donde esté el problema, sin mirar cada local por separado.',
        },
      ],
      ahorra: 'Una configuración para todo el grupo, no una por sede.',
    },
    {
      clave: 'publicaciones',
      captura: {
        ...SUGERENCIAS,
        alt: 'Las sugerencias de la IA para una publicación: tres textos propuestos a partir de la foto, la ficha de Google y la web, cada uno con su botón «Usar este texto».',
      },
      kicker: 'Cambios y publicaciones en bloque',
      titulo: 'Un cambio, todo el grupo actualizado',
      // OJO al verificar esto: NO hay endpoint masivo en el backend, y por eso
      // una revisión que solo mire ahí concluye que la función no existe (me
      // pasó). Vive en el cliente: `bulkSave` en `GMBProfile.jsx` recorre los
      // locales del scope y llama al endpoint de uno en uno, así que para el
      // usuario es edición en bloque.
      //
      // Lo que entra: Contacto, Categorías, Horarios, Comodidades y Fotos. Lo
      // que no: las pestañas Información y Acciones, que la propia página
      // esconde en multi-local (`showInfo`/`showActions` = `!isMulti`).
      p1: 'Si cae un festivo o cambias el horario de temporada, no hace falta entrar local por local. Eliges las sedes, lo cambias una vez y se aplica en todas: horarios, categorías, teléfonos, web, redes sociales y comodidades. Las fotos igual, subiendo una tanda a varios locales de golpe.',
      p2: 'Y las publicaciones se escriben una sola vez usando variables como el nombre del local, la ciudad o la dirección: salen adaptadas en cada sede sin repetir el trabajo. Nada de escribir la misma oferta veinte veces con veinte direcciones distintas.',
      bullets: [
        {
          clave: 'horarios', t: 'Horarios en bloque',
          d: 'Festivos y temporadas aplicados de golpe a las sedes que elijas.',
        },
        {
          clave: 'variables', t: 'Variables por sede',
          d: 'El nombre, la ciudad, la dirección, el teléfono y la web de cada local se rellenan solos.',
        },
        {
          clave: 'programacion', t: 'Programación por adelantado',
          d: 'El mes entero montado de una sentada, hasta con 180 días de antelación.',
        },
      ],
      ahorra: 'Veinte publicaciones en el tiempo de escribir una.',
    },
  ],

  informe: {
    captura: {
      ...INFORME,
      alt: 'Una página del informe mensual en PDF: la tasa de respuesta y el tiempo medio frente a su recomendado, y el análisis de sentimiento con lo que gusta y lo que mejorarían los clientes.',
    },
    titulo: 'Un informe de conjunto, y el detalle de cada sede si lo necesitas',
    p1: 'El día 1 de cada mes te llega un PDF con el rendimiento de todo tu grupo: reseñas, sentimiento, palabras clave de tus clientes y tus métricas de visibilidad, sumando todos tus locales. Y si necesitas bajar al detalle de una sede en concreto, tienes su informe individual con la misma profundidad, sin tener que sumarlo tú mismo local por local.',
    p2: 'Es el documento que puedes llevar a una reunión de dirección sin preparar nada: lo que ha pasado en todo el grupo el mes pasado, y en qué sede hay que poner el foco el mes que viene.',
  },

  logos: 'Algunas de las marcas que confían en MyTapstar para subir posiciones',

  faqTitulo: 'Preguntas frecuentes',
  faq: [
    {
      q: '¿Tengo que conectar cada local por separado?',
      a: 'Normalmente no desde cero: si ya tienes todas tus fichas en la misma cuenta de Google Business, las conectas juntas al vincular esa cuenta. Solo tocaría conectarlas una por una si están repartidas en cuentas de Google distintas.',
    },
    {
      q: '¿Los datos de un local se mezclan con los de otro?',
      a: 'No. Cada local mantiene sus propias reseñas, su propia puntuación y su propio escáner. Lo único que se agrega es lo que tú decides consultar: la cobertura de marca dentro de un escaneo, o el informe mensual de todo el grupo.',
    },
    {
      q: '¿Puedo tener locales con tonos de respuesta distintos dentro del mismo grupo?',
      a: 'Sí, cada local puede tener su propio tono, o puedes aplicar el mismo a todos si prefieres una sola voz de marca.',
    },
    {
      q: '¿Puedo cambiar el horario de todas mis sedes de una vez?',
      // Lo que NO entra en el bloque son las pestañas «Información» y
      // «Acciones», que `GMBProfile` deshabilita en modo multi-local.
      a: 'Sí. Eliges las sedes, haces el cambio una vez y se aplica en todas: horarios, categorías, teléfonos, web, redes sociales, comodidades y fotos. Lo que se sigue haciendo ficha a ficha son los datos propios de cada una —la descripción, la dirección— y los enlaces de acción. El nombre del negocio no se cambia desde aquí en ningún caso: Google lo pasa por revisión y hay que tocarlo en su panel.',
    },
    {
      q: '¿Hay un mínimo de locales para pasar por este formulario?',
      a: 'Si tienes menos de 5 locales, te va mejor empezar directo desde la landing principal con la prueba gratis de 7 días.',
    },
  ],
};

/*
 * Las reglas de traducción están en `landingV3.home.js`. Dos avisos propios de
 * esta página:
 *
 *  - **Las FAQ dicen lo que el producto hace, no lo que suena mejor.** Cada una
 *    se contrastó contra el código (la cabecera de este fichero explica cuáles
 *    y por qué). Traducirlas «redondeando» —que la edición en bloque llegue a
 *    todo, que el nombre del negocio se pueda cambiar— vuelve a meter la
 *    promesa falsa que se quitó, solo que en un idioma donde nadie la revisa.
 *  - **Las variables de publicación NO se traducen.** `{{name}}`, `{{address}}`
 *    y `{{city}}` son literales que el usuario escribe en el panel.
 */
const en = {
  hero: {
    badge: 'For brands and groups with several locations',
    titulo: 'Make every location, without exception, the ',
    acento: 'no. 1 in its area',
    sub: 'One dashboard to run all your listings, compare performance across locations and apply changes to your whole group in one click.',
    cta: 'Request more information',
    pieCaptura: 'The position scanner, exactly as you see it inside the dashboard',
    captura: {
      ...MAPA,
      alt: 'The SEO map in the dashboard: on the left the scan form with the chosen location and the results, on the right the grid of positions over the city map.',
    },
  },

  problema: {
    titulo: 'Ten locations, ten open tabs, and no overall picture',
    p1: 'Each location has its own listing, its own reviews, its own position on Google Maps. Without a dashboard that brings them together, running them means going in one by one, repeating the same task multiplied by every site, and trusting that nobody forgets one.',
    p2: 'And what you really lose is not just time. It is the overall picture: while you are busy with the location making the most noise, another one can spend weeks slipping down the rankings without anyone noticing, because nobody has a way to compare them all at once.',
    p3: 'With two locations you can still hold it together by hand. With five it gets hard, and past ten, running them stops being a task and becomes a job in itself: someone on your team spending hours repeating the same thing with twenty different passwords, with nobody able to say, with data, which site is doing well and which is not.',
  },

  horas: {
    cifra: 'Hundreds of hours',
    texto: 'that your team stops losing every year on tasks repeated location by location',
  },

  ahorraEtiqueta: 'What it saves you',
  secciones: [
    {
      clave: 'panel',
      captura: {
        ...BANDEJA,
        alt: 'The single review inbox: the list across all locations on the left and the open review on the right, with its reply.',
      },
      kicker: 'Central dashboard',
      titulo: 'All your locations, one single screen',
      p1: 'Every Google listing you manage lives in the same dashboard, with no signing in and out of one account after another. You pick the location from a selector and everything below is that site’s: its reviews, its position, its score.',
      p2: 'And the reviews from all your locations arrive in one inbox, so replying is not hopping from listing to listing — it is a single list you work through to the end.',
      bullets: [
        {
          clave: 'acceso', t: 'One single login',
          d: 'No handing out Google passwords to site managers.',
        },
        {
          clave: 'bandeja', t: 'Unified inbox',
          d: 'Every review arrives with its location’s name beside it, so you know where it came from without switching screens.',
        },
        {
          clave: 'permisos', t: 'Permissions per site',
          d: 'Each manager sees their own, and you see everything.',
        },
      ],
      ahorra: 'One session a day instead of one per location.',
    },
    {
      clave: 'comparar',
      captura: {
        ...RANKING,
        alt: 'The ranking across locations: four cards with each one’s SEO score and level, sorted best to worst.',
      },
      kicker: 'Compare and prioritise',
      titulo: 'You know exactly which one needs help today',
      p1: 'You see each location’s average position, side by side, without going in to look at them separately. While one is doing well another may be slipping, and this is where you notice before it is too late.',
      p2: 'You know which one to start with, instead of guessing or going through them one at a time to see whether something is wrong. And because the history is kept, you do not just see today’s snapshot: you see who is climbing and who is falling month by month.',
      bullets: [
        {
          clave: 'media', t: 'Average position per site',
          d: 'Each location’s average ranking, counting only the points where it appears.',
        },
        {
          clave: 'cobertura', t: 'Coverage and dominance',
          d: 'How many points each one shows up at, and at how many it is in the top three.',
        },
        {
          clave: 'historico', t: 'Month-by-month history',
          d: 'The trend for each site, to tell a bad day apart from a real decline.',
        },
      ],
      ahorra: 'Stop going through ten listings to find the one that is failing.',
    },
    {
      clave: 'escaner',
      captura: {
        ...MAPA,
        alt: 'The SEO map in the dashboard: on the left the scan form with the chosen location and the results, on the right the grid of positions over the city map.',
      },
      kicker: 'Scanner per location',
      titulo: 'Every location, with its own scanner and its own score',
      p1: 'Each site scans its own area separately, with the search its customers actually use and the grid size that makes sense for its neighbourhood. What you see for each one is its own: nothing gets mixed or diluted between sites.',
      p2: 'And every location also has its own SEO score from 0 to 100, with its level and its next recommended action, exactly as if you ran a single location.',
      bullets: [
        {
          clave: 'nivel', t: 'Its own score and level',
          d: 'From “Just starting” to “Local leader”, worked out from that listing’s real data.',
        },
        {
          clave: 'areas', t: 'Six areas measured',
          d: 'Reputation, activity, photos, categories, keywords and the listing’s details.',
        },
        {
          clave: 'accion', t: 'Next action per site',
          d: 'What lifts that particular location most, not generic advice for the whole group.',
        },
      ],
      ahorra: 'Each site’s diagnosis, done and ranked by impact.',
    },
    {
      clave: 'grupo',
      captura: {
        ...CUADRICULA,
        alt: 'The grid of positions in detail over the map: one disc per point with the ranking the business comes up at, from green to red.',
      },
      kicker: 'SEO map for your group',
      insignia: 'New',
      titulo: 'You see how your brand covers an area, not just how one location is doing',
      p1: 'As well as each site’s map, in every scan you can see your BRAND’s coverage in that area: at each point it takes the best ranking between the location you scanned and any other location of yours that shows up there. That way you see whether a corner is covered by your other site instead of writing it off.',
      p2: 'It is the difference between knowing that “the High Street location is doing well” and knowing which parts of that area you have covered with everything you have open around it.',
      bullets: [
        {
          clave: 'dominancia', t: 'Brand dominance',
          d: 'At what percentage of the points in that area your group is in the top three, not just the location you scanned.',
        },
        {
          clave: 'huecos', t: 'Coverage gaps',
          d: 'The points none of your locations reach, which is where it makes sense to grow.',
        },
        {
          clave: 'detalle', t: 'Without losing the detail',
          d: 'You still have the point-by-point map of each site whenever you want to go into the specifics.',
        },
      ],
      ahorra: 'A coverage figure you can take to management without working it out yourself.',
      nota: 'The count is conservative: the ranking of your other locations is read from the top 20 kept for each point, so one that falls below that counts as not showing. It never inflates the figure.',
    },
    {
      clave: 'automatizaciones',
      captura: {
        ...TONO,
        alt: 'The create-tone dialogue: you choose whether it applies to positive or critical reviews and to which locations, with the all-locations option ticked.',
      },
      kicker: 'Automations at scale',
      titulo: 'Set it up once, and it works across all your locations',
      p1: 'You choose whether each location replies in its own tone or whether they all speak with the brand’s. You set the rule up once and it applies at every site, without repeating the work location by location. And you decide which ratings get answered automatically and after how long: out of the box, 4 and 5 stars are automated and the low ones stay as an alert, which is the sensible setting, but you can automate right down to 1 star if you prefer.',
      p2: 'The words that matter to you are watched across the whole group at once: as soon as they show up in a new review at any of your locations, the email reaches you straight away. And with low-rating alerts, you hear about the bad review within minutes, whichever location it came from.',
      bullets: [
        {
          clave: 'tono', t: 'Its own tone, or the brand’s',
          d: 'Each site with its own voice, or a single voice across all of them if you prefer consistency.',
        },
        {
          clave: 'palabras', t: 'Shared keywords',
          d: 'One general rule watching the reviews at all your locations, and whatever exceptions you want per site.',
        },
        {
          clave: 'alertas', t: 'Immediate alerts',
          d: 'You react fast wherever the problem is, without checking each location separately.',
        },
      ],
      ahorra: 'One setup for the whole group, not one per site.',
    },
    {
      clave: 'publicaciones',
      captura: {
        ...SUGERENCIAS,
        alt: 'AI suggestions for a post: three drafts proposed from the photo, the Google profile and the website, each with a “Use this text” button.',
      },
      kicker: 'Bulk changes and posts',
      titulo: 'One change, the whole group updated',
      p1: 'If a bank holiday lands or you change the seasonal opening hours, there is no need to go in location by location. You pick the sites, change it once and it applies to them all: opening hours, categories, phone numbers, website, social profiles and amenities. Photos too, uploading one batch to several locations at once.',
      p2: 'And posts are written once using variables such as the location’s name, the city or the address: they come out adapted at each site without repeating the work. No writing the same offer twenty times with twenty different addresses.',
      bullets: [
        {
          clave: 'horarios', t: 'Bulk opening hours',
          d: 'Bank holidays and seasons applied in one go to the sites you choose.',
        },
        {
          clave: 'variables', t: 'Variables per site',
          d: 'Each location’s name, city, address, phone and website fill themselves in.',
        },
        {
          clave: 'programacion', t: 'Scheduled in advance',
          d: 'The whole month set up in one sitting, up to 180 days ahead.',
        },
      ],
      ahorra: 'Twenty posts in the time it takes to write one.',
    },
  ],

  informe: {
    captura: {
      ...INFORME,
      alt: 'A page of the monthly PDF report: the reply rate and average time against their recommended figures, and the sentiment analysis with what customers like and what they would improve.',
    },
    titulo: 'A group-wide report, and the detail of each site if you need it',
    p1: 'On the 1st of every month you get a PDF with the performance of your whole group: reviews, sentiment, your customers’ keywords and your visibility metrics, adding up all your locations. And if you need to go into the detail of one particular site, you have its individual report with the same depth, without having to add it up yourself location by location.',
    p2: 'It is the document you can take into a management meeting without preparing anything: what happened across the whole group last month, and which site to focus on next month.',
  },

  logos: 'Some of the brands that trust MyTapstar to climb positions',

  faqTitulo: 'Frequently asked questions',
  faq: [
    {
      q: 'Do I have to connect each location separately?',
      a: 'Usually not from scratch: if you already have all your listings in the same Google Business account, you connect them together when you link that account. You would only need to connect them one by one if they are spread across different Google accounts.',
    },
    {
      q: 'Does one location’s data get mixed up with another’s?',
      a: 'No. Each location keeps its own reviews, its own score and its own scanner. The only thing aggregated is what you choose to look at: brand coverage within a scan, or the monthly report for the whole group.',
    },
    {
      q: 'Can I have locations with different reply tones within the same group?',
      a: 'Yes, each location can have its own tone, or you can apply the same one to all of them if you prefer a single brand voice.',
    },
    {
      q: 'Can I change the opening hours of all my sites at once?',
      a: 'Yes. You pick the sites, make the change once and it applies to them all: opening hours, categories, phone numbers, website, social profiles, amenities and photos. What is still done listing by listing is each one’s own details — the description, the address — and the action links. The business name cannot be changed from here under any circumstances: Google puts it through review and it has to be edited in their dashboard.',
    },
    {
      q: 'Is there a minimum number of locations to go through this form?',
      a: 'If you have fewer than 5 locations, you are better off starting straight from the main landing page with the 7-day free trial.',
    },
  ],
};

const pt = {
  hero: {
    badge: 'Para marcas e grupos com vários locais',
    titulo: 'Que cada local, sem exceção, seja o ',
    acento: 'nº 1 da sua zona',
    sub: 'Um painel para controlar todas as suas fichas, comparar o desempenho entre locais e aplicar alterações a todo o grupo com um clique.',
    cta: 'Pedir mais informações',
    pieCaptura: 'O scanner de posições, tal como o vê dentro do painel',
    captura: {
      ...MAPA,
      alt: 'O Mapa de SEO do painel: à esquerda o formulário de análise com o local escolhido e os resultados, à direita a grelha de posições sobre o mapa da cidade.',
    },
  },

  problema: {
    titulo: 'Dez locais, dez separadores abertos, e nenhuma visão de conjunto',
    p1: 'Cada local tem a sua própria ficha, as suas próprias avaliações, a sua própria posição no Google Maps. Sem um painel que os junte, geri-los significa entrar um a um, repetir a mesma tarefa multiplicada por cada sede, e confiar que ninguém se esquece de nenhum.',
    p2: 'E o que se perde de verdade não é só tempo. É a visão de conjunto: enquanto está atento ao local que faz mais barulho, outro pode andar há semanas a cair posições sem que ninguém dê por isso, porque ninguém tem forma de comparar todos ao mesmo tempo.',
    p3: 'Com dois locais ainda se aguenta à mão. Com cinco já custa, e a partir de dez a gestão deixa de ser uma tarefa e passa a ser um trabalho em si mesmo: alguém da sua equipa a dedicar horas a repetir o mesmo com vinte palavras-passe diferentes, sem que ninguém consiga dizer com dados que sede vai bem e qual não.',
  },

  horas: {
    cifra: 'Centenas de horas',
    texto: 'que a sua equipa deixa de perder por ano em tarefas repetidas, local a local',
  },

  ahorraEtiqueta: 'O que lhe poupa',
  secciones: [
    {
      clave: 'panel',
      captura: {
        ...BANDEJA,
        alt: 'A caixa de entrada única de avaliações: a lista de todos os locais à esquerda e a avaliação aberta à direita, com a sua resposta.',
      },
      kicker: 'Painel centralizado',
      titulo: 'Todos os seus locais, um só ecrã',
      p1: 'Cada ficha do Google que gere vive no mesmo painel, sem entrar e sair de conta em conta. Escolhe o local num seletor e tudo o que vê por baixo é dessa sede: as suas avaliações, a sua posição, a sua pontuação.',
      p2: 'E as avaliações de todos os seus locais chegam a uma caixa de entrada única, por isso responder não é saltar de ficha em ficha, é uma só lista que vai esvaziando até ao fim.',
      bullets: [
        {
          clave: 'acceso', t: 'Um só acesso',
          d: 'Sem distribuir palavras-passe do Google pelos responsáveis de cada sede.',
        },
        {
          clave: 'bandeja', t: 'Caixa de entrada unificada',
          d: 'Cada avaliação chega com o nome do seu local ao lado, por isso sabe de onde vem sem mudar de ecrã.',
        },
        {
          clave: 'permisos', t: 'Permissões por sede',
          d: 'Cada responsável vê o que é seu, e você vê tudo.',
        },
      ],
      ahorra: 'Uma sessão por dia em vez de uma por local.',
    },
    {
      clave: 'comparar',
      captura: {
        ...RANKING,
        alt: 'O ranking entre locais: quatro cartões com a pontuação SEO de cada um e o seu nível, ordenados do melhor para o pior.',
      },
      kicker: 'Comparar e priorizar',
      titulo: 'Sabe exatamente qual precisa de ajuda hoje',
      p1: 'Vê a posição média de cada local, lado a lado, sem ter de os abrir em separado. Enquanto um vai bem, outro pode estar a cair, e é aqui que se nota antes de ser tarde.',
      p2: 'Sabe por qual começar, em vez de adivinhar ou rever local a local a ver se algo falha. E como o histórico fica guardado, não vê só a fotografia de hoje: vê quem sobe e quem desce mês a mês.',
      bullets: [
        {
          clave: 'media', t: 'Posição média por sede',
          d: 'A posição média de cada local contando apenas os pontos onde aparece.',
        },
        {
          clave: 'cobertura', t: 'Cobertura e domínio',
          d: 'Em quantos pontos aparece cada um, e em quantos está entre os três primeiros.',
        },
        {
          clave: 'historico', t: 'Histórico mês a mês',
          d: 'A tendência de cada sede, para distinguir um mau dia de uma queda a sério.',
        },
      ],
      ahorra: 'Deixa de rever dez fichas para encontrar a que falha.',
    },
    {
      clave: 'escaner',
      captura: {
        ...MAPA,
        alt: 'O Mapa de SEO do painel: à esquerda o formulário de análise com o local escolhido e os resultados, à direita a grelha de posições sobre o mapa da cidade.',
      },
      kicker: 'Scanner por local',
      titulo: 'Cada local, com o seu próprio scanner e a sua própria pontuação',
      p1: 'Cada sede analisa a sua zona em separado, com a pesquisa que os seus clientes usam mesmo e o tamanho de grelha que faça sentido para o seu bairro. O que vê de cada uma é o que é seu: nada se mistura nem se dilui entre sedes.',
      p2: 'E cada local tem também a sua própria pontuação SEO de 0 a 100, com o seu nível e a sua próxima ação recomendada, exatamente como se gerisse um só local.',
      bullets: [
        {
          clave: 'nivel', t: 'Pontuação e nível próprios',
          d: 'De «A começar» a «Líder local», calculado com os dados reais dessa ficha.',
        },
        {
          clave: 'areas', t: 'Seis áreas medidas',
          d: 'Reputação, atividade, fotografias, categorias, palavras-chave e dados da ficha.',
        },
        {
          clave: 'accion', t: 'Próxima ação por sede',
          d: 'O que mais faz subir esse local em concreto, não um conselho genérico para todo o grupo.',
        },
      ],
      ahorra: 'O diagnóstico de cada sede, feito e ordenado por impacto.',
    },
    {
      clave: 'grupo',
      captura: {
        ...CUADRICULA,
        alt: 'A grelha de posições em detalhe sobre o mapa: um disco por ponto com a posição em que o negócio aparece, do verde ao vermelho.',
      },
      kicker: 'Mapa de SEO do seu grupo',
      insignia: 'Novo',
      titulo: 'Vê como a sua marca cobre uma zona, não só como vai um local',
      p1: 'Além do mapa de cada sede, em cada análise pode ver a cobertura da sua MARCA nessa zona: em cada ponto toma-se a melhor posição entre a do local que analisou e a de qualquer outro local seu que apareça ali. Assim vê se uma esquina está coberta pela sua outra sede em vez de a dar por perdida.',
      p2: 'É a diferença entre saber que «o local da rua principal vai bem» e saber que partes dessa zona tem cobertas com tudo o que tem aberto à volta.',
      bullets: [
        {
          clave: 'dominancia', t: 'Domínio de marca',
          d: 'Em que percentagem dos pontos dessa zona o seu grupo está entre os três primeiros, e não só o local que analisou.',
        },
        {
          clave: 'huecos', t: 'Falhas de cobertura',
          d: 'Os pontos onde não chega nenhum dos seus locais, que é onde faz sentido crescer.',
        },
        {
          clave: 'detalle', t: 'Sem perder o detalhe',
          d: 'Continua a ter o mapa ponto a ponto de cada sede quando quiser descer ao caso concreto.',
        },
      ],
      ahorra: 'Um número de cobertura que pode levar à direção sem o calcular.',
      nota: 'A conta é conservadora: a posição dos seus outros locais lê-se do top 20 que se guarda de cada ponto, por isso um que fique mais abaixo conta como não aparecendo. Nunca inflaciona o dado.',
    },
    {
      clave: 'automatizaciones',
      captura: {
        ...TONO,
        alt: 'A janela de criar tom: escolhe-se se se aplica às avaliações positivas ou críticas e a que locais, com a opção de todos assinalada.',
      },
      kicker: 'Automatizações à escala',
      titulo: 'Configure uma vez, e funciona em todos os seus locais',
      p1: 'Escolhe se cada local responde com o seu próprio tom ou se todos falam com o da marca. Configura a regra uma vez e aplica-se em cada sede, sem repetir o trabalho local a local. E decide que classificações se respondem sozinhas e com que atraso: de origem automatizam-se as de 4 e 5 estrelas e as baixas ficam em aviso, que é o sensato, mas pode automatizar até a de 1 estrela se preferir.',
      p2: 'As palavras que lhe importam são vigiadas em todo o grupo ao mesmo tempo: assim que aparecem numa avaliação nova de qualquer um dos seus locais, chega-lhe o email de imediato. E com os alertas por classificação baixa, fica a saber da avaliação má em minutos, seja do local que for.',
      bullets: [
        {
          clave: 'tono', t: 'Tom próprio ou de marca',
          d: 'Cada sede com a sua voz, ou uma só voz em todas se preferir coerência.',
        },
        {
          clave: 'palabras', t: 'Palavras-chave partilhadas',
          d: 'Uma regra geral que vigia as avaliações de todos os seus locais, e as exceções que quiser por sede.',
        },
        {
          clave: 'alertas', t: 'Alertas imediatos',
          d: 'Reage depressa esteja o problema onde estiver, sem olhar para cada local em separado.',
        },
      ],
      ahorra: 'Uma configuração para todo o grupo, não uma por sede.',
    },
    {
      clave: 'publicaciones',
      captura: {
        ...SUGERENCIAS,
        alt: 'As sugestões da IA para uma publicação: três textos propostos a partir da foto, da ficha do Google e do site, cada um com o seu botão «Usar este texto».',
      },
      kicker: 'Alterações e publicações em bloco',
      titulo: 'Uma alteração, todo o grupo atualizado',
      p1: 'Se calha um feriado ou muda o horário de época, não é preciso entrar local a local. Escolhe as sedes, altera uma vez e aplica-se em todas: horários, categorias, telefones, site, redes sociais e comodidades. As fotografias igual, carregando um lote para vários locais de uma vez.',
      p2: 'E as publicações escrevem-se uma só vez usando variáveis como o nome do local, a cidade ou a morada: saem adaptadas em cada sede sem repetir o trabalho. Nada de escrever a mesma promoção vinte vezes com vinte moradas diferentes.',
      bullets: [
        {
          clave: 'horarios', t: 'Horários em bloco',
          d: 'Feriados e épocas aplicados de uma vez às sedes que escolher.',
        },
        {
          clave: 'variables', t: 'Variáveis por sede',
          d: 'O nome, a cidade, a morada, o telefone e o site de cada local preenchem-se sozinhos.',
        },
        {
          clave: 'programacion', t: 'Agendamento com antecedência',
          d: 'O mês inteiro montado de uma vez, até com 180 dias de antecedência.',
        },
      ],
      ahorra: 'Vinte publicações no tempo de escrever uma.',
    },
  ],

  informe: {
    captura: {
      ...INFORME,
      alt: 'Uma página do relatório mensal em PDF: a taxa de resposta e o tempo médio face ao recomendado, e a análise de sentimento com o que os clientes gostam e o que melhorariam.',
    },
    titulo: 'Um relatório de conjunto, e o detalhe de cada sede se precisar',
    p1: 'No dia 1 de cada mês recebe um PDF com o desempenho de todo o seu grupo: avaliações, sentimento, palavras-chave dos seus clientes e as suas métricas de visibilidade, somando todos os seus locais. E se precisar de descer ao detalhe de uma sede em concreto, tem o relatório individual com a mesma profundidade, sem ter de o somar local a local.',
    p2: 'É o documento que pode levar a uma reunião de direção sem preparar nada: o que aconteceu em todo o grupo no mês passado, e em que sede pôr o foco no mês seguinte.',
  },

  logos: 'Algumas das marcas que confiam no MyTapstar para subir posições',

  faqTitulo: 'Perguntas frequentes',
  faq: [
    {
      q: 'Tenho de ligar cada local em separado?',
      a: 'Normalmente não de raiz: se já tem todas as suas fichas na mesma conta do Google Business, liga-as em conjunto ao associar essa conta. Só teria de as ligar uma a uma se estiverem repartidas por contas do Google diferentes.',
    },
    {
      q: 'Os dados de um local misturam-se com os de outro?',
      a: 'Não. Cada local mantém as suas próprias avaliações, a sua própria pontuação e o seu próprio scanner. A única coisa que se agrega é o que decidir consultar: a cobertura de marca dentro de uma análise, ou o relatório mensal de todo o grupo.',
    },
    {
      q: 'Posso ter locais com tons de resposta diferentes dentro do mesmo grupo?',
      a: 'Sim, cada local pode ter o seu próprio tom, ou pode aplicar o mesmo a todos se preferir uma só voz de marca.',
    },
    {
      q: 'Posso mudar o horário de todas as minhas sedes de uma vez?',
      a: 'Sim. Escolhe as sedes, faz a alteração uma vez e aplica-se em todas: horários, categorias, telefones, site, redes sociais, comodidades e fotografias. O que se continua a fazer ficha a ficha são os dados próprios de cada uma — a descrição, a morada — e os links de ação. O nome do negócio não se muda daqui em caso algum: o Google passa-o por revisão e tem de ser alterado no painel deles.',
    },
    {
      q: 'Há um mínimo de locais para passar por este formulário?',
      a: 'Se tem menos de 5 locais, é melhor começar diretamente pela página principal com o teste grátis de 7 dias.',
    },
  ],
};

const de = {
  hero: {
    badge: 'Für Marken und Gruppen mit mehreren Standorten',
    titulo: 'Mach jeden Standort, ohne Ausnahme, zur ',
    acento: 'Nr. 1 seiner Gegend',
    sub: 'Ein Dashboard, um alle deine Profile zu steuern, die Leistung zwischen Standorten zu vergleichen und Änderungen mit einem Klick auf die ganze Gruppe anzuwenden.',
    cta: 'Mehr Informationen anfordern',
    pieCaptura: 'Der Positions-Scanner, genau so, wie du ihn im Dashboard siehst',
    captura: {
      ...MAPA,
      alt: 'Die SEO-Karte im Dashboard: links das Scan-Formular mit dem gewählten Standort und den Ergebnissen, rechts das Raster der Positionen über der Stadtkarte.',
    },
  },

  problema: {
    titulo: 'Zehn Standorte, zehn offene Tabs und kein Gesamtbild',
    p1: 'Jeder Standort hat sein eigenes Profil, seine eigenen Bewertungen, seine eigene Position bei Google Maps. Ohne ein Dashboard, das sie zusammenführt, heißt sie zu verwalten: einzeln einloggen, dieselbe Aufgabe mal Anzahl der Standorte wiederholen und darauf vertrauen, dass niemand einen vergisst.',
    p2: 'Und was dabei wirklich verloren geht, ist nicht nur Zeit. Es ist das Gesamtbild: Während du dich um den Standort kümmerst, der am lautesten ist, kann ein anderer seit Wochen Positionen verlieren, ohne dass es jemand merkt — weil niemand alle auf einmal vergleichen kann.',
    p3: 'Mit zwei Standorten geht es von Hand noch. Mit fünf wird es mühsam, und ab zehn ist die Verwaltung keine Aufgabe mehr, sondern eine Arbeit für sich: jemand aus deinem Team verbringt Stunden damit, dasselbe mit zwanzig verschiedenen Passwörtern zu wiederholen, ohne dass irgendwer mit Daten sagen könnte, welcher Standort gut läuft und welcher nicht.',
  },

  horas: {
    cifra: 'Hunderte Stunden',
    texto: 'die dein Team pro Jahr nicht mehr für Aufgaben verliert, die es Standort für Standort wiederholt',
  },

  ahorraEtiqueta: 'Was es dir spart',
  secciones: [
    {
      clave: 'panel',
      captura: {
        ...BANDEJA,
        alt: 'Der gemeinsame Bewertungs-Posteingang: links die Liste aller Standorte, rechts die geöffnete Bewertung mit ihrer Antwort.',
      },
      kicker: 'Zentrales Dashboard',
      titulo: 'Alle deine Standorte, ein einziger Bildschirm',
      p1: 'Jedes Google-Profil, das du verwaltest, liegt im selben Dashboard — ohne dich von Konto zu Konto ein- und auszuloggen. Du wählst den Standort über eine Auswahl, und alles darunter gehört zu diesem Standort: seine Bewertungen, seine Position, seine Punktzahl.',
      p2: 'Und die Bewertungen all deiner Standorte laufen in einem Posteingang auf. Antworten heißt also nicht, von Profil zu Profil zu springen, sondern eine einzige Liste bis zum Ende abzuarbeiten.',
      bullets: [
        {
          clave: 'acceso', t: 'Ein einziger Zugang',
          d: 'Ohne Google-Passwörter an die Standortverantwortlichen zu verteilen.',
        },
        {
          clave: 'bandeja', t: 'Gemeinsamer Posteingang',
          d: 'Jede Bewertung kommt mit dem Namen ihres Standorts daneben an — du weißt also, woher sie kommt, ohne den Bildschirm zu wechseln.',
        },
        {
          clave: 'permisos', t: 'Rechte pro Standort',
          d: 'Jede verantwortliche Person sieht das Eigene, und du siehst alles.',
        },
      ],
      ahorra: 'Eine Sitzung am Tag statt einer pro Standort.',
    },
    {
      clave: 'comparar',
      captura: {
        ...RANKING,
        alt: 'Das Ranking zwischen den Standorten: vier Karten mit der SEO-Punktzahl und dem Level jedes Standorts, vom besten zum schlechtesten sortiert.',
      },
      kicker: 'Vergleichen und priorisieren',
      titulo: 'Du weißt genau, welcher heute Hilfe braucht',
      p1: 'Du siehst die durchschnittliche Position jedes Standorts nebeneinander, ohne sie einzeln aufzurufen. Während einer gut läuft, kann ein anderer abrutschen — und hier fällt es auf, bevor es zu spät ist.',
      p2: 'Du weißt, wo du anfängst, statt zu raten oder Standort für Standort nachzusehen, ob etwas schiefläuft. Und weil die Historie gespeichert wird, siehst du nicht nur die Momentaufnahme von heute: du siehst, wer Monat für Monat steigt und wer fällt.',
      bullets: [
        {
          clave: 'media', t: 'Durchschnittsposition pro Standort',
          d: 'Die mittlere Position jedes Standorts, gezählt nur an den Punkten, an denen er erscheint.',
        },
        {
          clave: 'cobertura', t: 'Abdeckung und Dominanz',
          d: 'An wie vielen Punkten jeder erscheint und an wie vielen er unter den ersten drei ist.',
        },
        {
          clave: 'historico', t: 'Historie Monat für Monat',
          d: 'Der Trend jedes Standorts, um einen schlechten Tag von einem echten Absturz zu unterscheiden.',
        },
      ],
      ahorra: 'Schluss damit, zehn Profile durchzugehen, um das eine zu finden, das hakt.',
    },
    {
      clave: 'escaner',
      captura: {
        ...MAPA,
        alt: 'Die SEO-Karte im Dashboard: links das Scan-Formular mit dem gewählten Standort und den Ergebnissen, rechts das Raster der Positionen über der Stadtkarte.',
      },
      kicker: 'Scanner pro Standort',
      titulo: 'Jeder Standort mit eigenem Scanner und eigener Punktzahl',
      p1: 'Jeder Standort scannt seine Gegend separat — mit der Suche, die seine Kunden wirklich benutzen, und der Rastergröße, die für sein Viertel Sinn ergibt. Was du zu jedem siehst, ist seins: nichts vermischt oder verwässert sich zwischen den Standorten.',
      p2: 'Und jeder Standort hat außerdem seine eigene SEO-Punktzahl von 0 bis 100, mit seinem Level und seiner nächsten empfohlenen Maßnahme — genau so, als würdest du einen einzigen Standort verwalten.',
      bullets: [
        {
          clave: 'nivel', t: 'Eigene Punktzahl und eigenes Level',
          d: 'Von „Am Anfang“ bis „Lokaler Marktführer“, berechnet aus den echten Daten dieses Profils.',
        },
        {
          clave: 'areas', t: 'Sechs gemessene Bereiche',
          d: 'Reputation, Aktivität, Fotos, Kategorien, Keywords und die Daten des Profils.',
        },
        {
          clave: 'accion', t: 'Nächste Maßnahme pro Standort',
          d: 'Was genau diesen Standort am meisten nach oben bringt, kein allgemeiner Rat für die ganze Gruppe.',
        },
      ],
      ahorra: 'Die Diagnose jedes Standorts, fertig und nach Wirkung sortiert.',
    },
    {
      clave: 'grupo',
      captura: {
        ...CUADRICULA,
        alt: 'Das Raster der Positionen im Detail über der Karte: eine Scheibe pro Punkt mit der Position, auf der das Unternehmen erscheint, von Grün bis Rot.',
      },
      kicker: 'SEO-Karte deiner Gruppe',
      insignia: 'Neu',
      titulo: 'Du siehst, wie deine Marke eine Gegend abdeckt, nicht nur wie ein Standort läuft',
      p1: 'Zusätzlich zur Karte jedes Standorts kannst du in jedem Scan die Abdeckung deiner MARKE in dieser Gegend sehen: An jedem Punkt wird die beste Position zwischen dem gescannten Standort und jedem anderen Standort von dir genommen, der dort auftaucht. So siehst du, ob eine Ecke von deinem anderen Standort abgedeckt wird, statt sie verloren zu geben.',
      p2: 'Das ist der Unterschied zwischen „der Standort in der Hauptstraße läuft gut“ und dem Wissen, welche Teile dieser Gegend du mit allem abgedeckt hast, was du drumherum offen hast.',
      bullets: [
        {
          clave: 'dominancia', t: 'Markendominanz',
          d: 'An wie viel Prozent der Punkte dieser Gegend deine Gruppe unter den ersten drei ist — nicht nur der gescannte Standort.',
        },
        {
          clave: 'huecos', t: 'Lücken in der Abdeckung',
          d: 'Die Punkte, die keiner deiner Standorte erreicht: dort ergibt Wachstum Sinn.',
        },
        {
          clave: 'detalle', t: 'Ohne das Detail zu verlieren',
          d: 'Du hast weiterhin die Punkt-für-Punkt-Karte jedes Standorts, wenn du ins Konkrete gehen willst.',
        },
      ],
      ahorra: 'Eine Abdeckungszahl, die du der Geschäftsführung vorlegen kannst, ohne sie selbst zu rechnen.',
      nota: 'Die Rechnung ist konservativ: Die Position deiner anderen Standorte wird aus den Top 20 gelesen, die pro Punkt gespeichert werden — wer darunter liegt, zählt als nicht erschienen. Die Zahl wird nie geschönt.',
    },
    {
      clave: 'automatizaciones',
      captura: {
        ...TONO,
        alt: 'Der Dialog zum Anlegen eines Tons: Man wählt, ob er für positive oder kritische Bewertungen gilt und für welche Standorte — mit der Option „alle“ angehakt.',
      },
      kicker: 'Automatisierungen im großen Maßstab',
      titulo: 'Einmal einrichten, und es läuft an allen deinen Standorten',
      p1: 'Du wählst, ob jeder Standort in seinem eigenen Ton antwortet oder ob alle mit dem der Marke sprechen. Du richtest die Regel einmal ein und sie gilt an jedem Standort, ohne die Arbeit Standort für Standort zu wiederholen. Und du entscheidest, welche Bewertungen von allein beantwortet werden und mit welcher Verzögerung: standardmäßig werden 4 und 5 Sterne automatisiert und die niedrigen bleiben als Hinweis stehen, was sinnvoll ist — du kannst aber bis hinunter zu 1 Stern automatisieren, wenn du willst.',
      p2: 'Die Wörter, die dir wichtig sind, werden in der ganzen Gruppe gleichzeitig überwacht: Sobald sie in einer neuen Bewertung an irgendeinem deiner Standorte auftauchen, kommt die E-Mail sofort. Und mit den Hinweisen bei niedriger Bewertung erfährst du innerhalb von Minuten von der schlechten Bewertung, egal von welchem Standort.',
      bullets: [
        {
          clave: 'tono', t: 'Eigener Ton oder Markenton',
          d: 'Jeder Standort mit seiner Stimme, oder eine einzige Stimme überall, wenn du Einheitlichkeit willst.',
        },
        {
          clave: 'palabras', t: 'Gemeinsame Keywords',
          d: 'Eine allgemeine Regel, die die Bewertungen all deiner Standorte überwacht, und die Ausnahmen, die du pro Standort willst.',
        },
        {
          clave: 'alertas', t: 'Sofortige Hinweise',
          d: 'Du reagierst schnell, wo auch immer das Problem sitzt, ohne jeden Standort einzeln nachzusehen.',
        },
      ],
      ahorra: 'Eine Einrichtung für die ganze Gruppe, nicht eine pro Standort.',
    },
    {
      clave: 'publicaciones',
      captura: {
        ...SUGERENCIAS,
        alt: 'KI-Vorschläge für einen Beitrag: drei Textentwürfe aus dem Foto, dem Google-Profil und der Website, jeder mit der Schaltfläche „Diesen Text verwenden“.',
      },
      kicker: 'Änderungen und Beiträge im Block',
      titulo: 'Eine Änderung, die ganze Gruppe aktualisiert',
      p1: 'Wenn ein Feiertag ansteht oder du die Öffnungszeiten der Saison änderst, musst du nicht Standort für Standort hinein. Du wählst die Standorte, änderst es einmal und es gilt überall: Öffnungszeiten, Kategorien, Telefonnummern, Website, soziale Netzwerke und Ausstattung. Fotos genauso — du lädst einen Schwung auf mehrere Standorte gleichzeitig hoch.',
      p2: 'Und Beiträge schreibst du ein einziges Mal mit Variablen wie dem Namen des Standorts, der Stadt oder der Adresse: Sie kommen an jedem Standort angepasst heraus, ohne die Arbeit zu wiederholen. Kein zwanzigmaliges Schreiben desselben Angebots mit zwanzig verschiedenen Adressen.',
      bullets: [
        {
          clave: 'horarios', t: 'Öffnungszeiten im Block',
          d: 'Feiertage und Saisons auf einen Schlag auf die Standorte angewendet, die du wählst.',
        },
        {
          clave: 'variables', t: 'Variablen pro Standort',
          d: 'Name, Stadt, Adresse, Telefon und Website jedes Standorts füllen sich von allein aus.',
        },
        {
          clave: 'programacion', t: 'Vorausgeplant',
          d: 'Der ganze Monat in einem Rutsch aufgesetzt, bis zu 180 Tage im Voraus.',
        },
      ],
      ahorra: 'Zwanzig Beiträge in der Zeit, die einer braucht.',
    },
  ],

  informe: {
    captura: {
      ...INFORME,
      alt: 'Eine Seite des monatlichen PDF-Berichts: Antwortquote und Durchschnittszeit gegenüber ihrem Richtwert, und die Stimmungsanalyse mit dem, was Kunden gefällt und was sie verbessern würden.',
    },
    titulo: 'Ein Gesamtbericht, und das Detail jedes Standorts, wenn du es brauchst',
    p1: 'Am 1. jedes Monats bekommst du ein PDF mit der Leistung deiner ganzen Gruppe: Bewertungen, Stimmung, Keywords deiner Kunden und deine Sichtbarkeitszahlen, über alle Standorte zusammengerechnet. Und wenn du ins Detail eines bestimmten Standorts willst, hast du seinen Einzelbericht in derselben Tiefe, ohne ihn selbst Standort für Standort zusammenrechnen zu müssen.',
    p2: 'Es ist das Dokument, das du ohne Vorbereitung in eine Leitungsrunde mitnehmen kannst: was letzten Monat in der ganzen Gruppe passiert ist und auf welchen Standort im nächsten der Fokus gehört.',
  },

  logos: 'Einige der Marken, die MyTapstar vertrauen, um Positionen gutzumachen',

  faqTitulo: 'Häufige Fragen',
  faq: [
    {
      q: 'Muss ich jeden Standort einzeln verbinden?',
      a: 'Normalerweise nicht von Grund auf: Wenn alle deine Profile schon im selben Google-Business-Konto liegen, verbindest du sie gemeinsam, sobald du dieses Konto verknüpfst. Einzeln verbinden müsstest du sie nur, wenn sie auf verschiedene Google-Konten verteilt sind.',
    },
    {
      q: 'Vermischen sich die Daten eines Standorts mit denen eines anderen?',
      a: 'Nein. Jeder Standort behält seine eigenen Bewertungen, seine eigene Punktzahl und seinen eigenen Scanner. Zusammengefasst wird nur das, was du bewusst abrufst: die Markenabdeckung innerhalb eines Scans oder der Monatsbericht der ganzen Gruppe.',
    },
    {
      q: 'Kann ich innerhalb derselben Gruppe Standorte mit unterschiedlichen Antworttönen haben?',
      a: 'Ja, jeder Standort kann seinen eigenen Ton haben — oder du wendest denselben auf alle an, wenn du eine einzige Markenstimme willst.',
    },
    {
      q: 'Kann ich die Öffnungszeiten aller meiner Standorte auf einmal ändern?',
      a: 'Ja. Du wählst die Standorte, änderst es einmal und es gilt überall: Öffnungszeiten, Kategorien, Telefonnummern, Website, soziale Netzwerke, Ausstattung und Fotos. Weiterhin Profil für Profil gemacht werden die eigenen Angaben jedes Standorts — die Beschreibung, die Adresse — und die Aktionslinks. Der Name des Unternehmens lässt sich von hier aus in keinem Fall ändern: Google schickt ihn durch eine Prüfung, und er muss in deren Dashboard bearbeitet werden.',
    },
    {
      q: 'Gibt es eine Mindestanzahl an Standorten für dieses Formular?',
      a: 'Wenn du weniger als 5 Standorte hast, fährst du besser damit, direkt auf der Hauptseite mit der kostenlosen 7-Tage-Testphase anzufangen.',
    },
  ],
};

const fr = {
  hero: {
    badge: 'Pour les marques et les groupes à plusieurs établissements',
    titulo: 'Que chaque établissement, sans exception, soit le ',
    acento: 'nº 1 de sa zone',
    sub: 'Un tableau de bord pour piloter toutes vos fiches, comparer les performances entre établissements et appliquer des changements à tout votre groupe en un clic.',
    cta: 'Demander plus d’informations',
    pieCaptura: 'Le scanner de positions, tel que vous le voyez dans le tableau de bord',
    captura: {
      ...MAPA,
      alt: 'La carte SEO du tableau de bord : à gauche le formulaire d’analyse avec l’établissement choisi et les résultats, à droite la grille des positions sur la carte de la ville.',
    },
  },

  problema: {
    titulo: 'Dix établissements, dix onglets ouverts, et aucune vision d’ensemble',
    p1: 'Chaque établissement a sa propre fiche, ses propres avis, sa propre position sur Google Maps. Sans un tableau de bord qui les réunit, les gérer veut dire y entrer un par un, répéter la même tâche multipliée par chaque site, et espérer que personne n’en oublie un.',
    p2: 'Et ce que l’on perd vraiment, ce n’est pas que du temps. C’est la vision d’ensemble : pendant que vous vous occupez de l’établissement qui fait le plus de bruit, un autre peut perdre des positions depuis des semaines sans que personne s’en aperçoive, parce que personne n’a le moyen de les comparer tous à la fois.',
    p3: 'Avec deux établissements, cela tient encore à la main. Avec cinq, cela devient lourd, et au-delà de dix, la gestion cesse d’être une tâche pour devenir un travail à part entière : quelqu’un de votre équipe passe des heures à répéter la même chose avec vingt mots de passe différents, sans que personne puisse dire, chiffres à l’appui, quel site va bien et lequel non.',
  },

  horas: {
    cifra: 'Des centaines d’heures',
    texto: 'que votre équipe ne perd plus chaque année en tâches répétées, établissement par établissement',
  },

  ahorraEtiqueta: 'Ce que cela vous fait gagner',
  secciones: [
    {
      clave: 'panel',
      captura: {
        ...BANDEJA,
        alt: 'La boîte de réception unique des avis : la liste de tous les établissements à gauche et l’avis ouvert à droite, avec sa réponse.',
      },
      kicker: 'Tableau de bord centralisé',
      titulo: 'Tous vos établissements, un seul écran',
      p1: 'Chaque fiche Google que vous gérez vit dans le même tableau de bord, sans entrer et sortir d’un compte à l’autre. Vous choisissez l’établissement dans un sélecteur et tout ce qui s’affiche en dessous est le sien : ses avis, sa position, sa note.',
      p2: 'Et les avis de tous vos établissements arrivent dans une seule boîte : répondre ne consiste plus à sauter de fiche en fiche, c’est une seule liste que vous videz jusqu’au bout.',
      bullets: [
        {
          clave: 'acceso', t: 'Un seul accès',
          d: 'Sans distribuer les mots de passe Google aux responsables de site.',
        },
        {
          clave: 'bandeja', t: 'Boîte unifiée',
          d: 'Chaque avis arrive avec le nom de son établissement à côté : vous savez d’où il vient sans changer d’écran.',
        },
        {
          clave: 'permisos', t: 'Droits par site',
          d: 'Chaque responsable voit le sien, et vous voyez tout.',
        },
      ],
      ahorra: 'Une session par jour au lieu d’une par établissement.',
    },
    {
      clave: 'comparar',
      captura: {
        ...RANKING,
        alt: 'Le classement entre établissements : quatre cartes avec la note SEO de chacun et son niveau, du meilleur au moins bon.',
      },
      kicker: 'Comparer et prioriser',
      titulo: 'Vous savez exactement lequel a besoin d’aide aujourd’hui',
      p1: 'Vous voyez la position moyenne de chaque établissement, côte à côte, sans aller les consulter séparément. Pendant que l’un va bien, un autre peut être en train de reculer, et c’est ici que cela se voit avant qu’il ne soit trop tard.',
      p2: 'Vous savez par lequel commencer, au lieu de deviner ou de passer les établissements en revue un à un pour voir si quelque chose cloche. Et comme l’historique est conservé, vous ne voyez pas que la photo du jour : vous voyez qui monte et qui descend mois après mois.',
      bullets: [
        {
          clave: 'media', t: 'Position moyenne par site',
          d: 'La position moyenne de chaque établissement, en ne comptant que les points où il apparaît.',
        },
        {
          clave: 'cobertura', t: 'Couverture et domination',
          d: 'À combien de points chacun apparaît, et à combien il est dans les trois premiers.',
        },
        {
          clave: 'historico', t: 'Historique mois par mois',
          d: 'La tendance de chaque site, pour distinguer un mauvais jour d’une vraie chute.',
        },
      ],
      ahorra: 'Fini de passer dix fiches en revue pour trouver celle qui coince.',
    },
    {
      clave: 'escaner',
      captura: {
        ...MAPA,
        alt: 'La carte SEO du tableau de bord : à gauche le formulaire d’analyse avec l’établissement choisi et les résultats, à droite la grille des positions sur la carte de la ville.',
      },
      kicker: 'Scanner par établissement',
      titulo: 'Chaque établissement, avec son propre scanner et sa propre note',
      p1: 'Chaque site analyse sa zone séparément, avec la recherche que ses clients utilisent vraiment et la taille de grille qui a du sens pour son quartier. Ce que vous voyez de chacun est le sien : rien ne se mélange ni ne se dilue entre les sites.',
      p2: 'Et chaque établissement a aussi sa propre note SEO de 0 à 100, avec son niveau et sa prochaine action recommandée, exactement comme si vous ne gériez qu’un seul établissement.',
      bullets: [
        {
          clave: 'nivel', t: 'Note et niveau propres',
          d: 'De « Au démarrage » à « Leader local », calculé à partir des données réelles de cette fiche.',
        },
        {
          clave: 'areas', t: 'Six domaines mesurés',
          d: 'Réputation, activité, photos, catégories, mots-clés et informations de la fiche.',
        },
        {
          clave: 'accion', t: 'Prochaine action par site',
          d: 'Ce qui fait le plus monter cet établissement précis, pas un conseil générique pour tout le groupe.',
        },
      ],
      ahorra: 'Le diagnostic de chaque site, fait et classé par impact.',
    },
    {
      clave: 'grupo',
      captura: {
        ...CUADRICULA,
        alt: 'La grille des positions en détail sur la carte : un disque par point avec la position à laquelle l’établissement apparaît, du vert au rouge.',
      },
      kicker: 'Carte SEO de votre groupe',
      insignia: 'Nouveau',
      titulo: 'Vous voyez comment votre marque couvre une zone, pas seulement comment va un établissement',
      p1: 'En plus de la carte de chaque site, dans chaque analyse vous pouvez voir la couverture de votre MARQUE dans cette zone : à chaque point, on retient la meilleure position entre celle de l’établissement analysé et celle de n’importe quel autre de vos établissements qui y apparaît. Vous voyez ainsi si un coin est couvert par votre autre site au lieu de le considérer perdu.',
      p2: 'C’est la différence entre savoir que « l’établissement de la rue principale va bien » et savoir quelles parties de cette zone vous couvrez avec tout ce que vous avez ouvert autour.',
      bullets: [
        {
          clave: 'dominancia', t: 'Domination de marque',
          d: 'Sur quel pourcentage des points de cette zone votre groupe est dans les trois premiers, et pas seulement l’établissement analysé.',
        },
        {
          clave: 'huecos', t: 'Trous de couverture',
          d: 'Les points qu’aucun de vos établissements n’atteint, c’est-à-dire là où il est pertinent de grandir.',
        },
        {
          clave: 'detalle', t: 'Sans perdre le détail',
          d: 'Vous gardez la carte point par point de chaque site quand vous voulez descendre au cas concret.',
        },
      ],
      ahorra: 'Un chiffre de couverture que vous pouvez présenter à la direction sans le calculer vous-même.',
      nota: 'Le calcul est prudent : la position de vos autres établissements est lue dans le top 20 conservé pour chaque point, donc un établissement classé en dessous compte comme absent. Le chiffre n’est jamais gonflé.',
    },
    {
      clave: 'automatizaciones',
      captura: {
        ...TONO,
        alt: 'La fenêtre de création de ton : on choisit si elle s’applique aux avis positifs ou critiques et à quels établissements, avec l’option « tous » cochée.',
      },
      kicker: 'Automatisations à grande échelle',
      titulo: 'Configurez une fois, et cela fonctionne dans tous vos établissements',
      p1: 'Vous choisissez si chaque établissement répond avec son propre ton ou si tous parlent avec celui de la marque. Vous configurez la règle une fois et elle s’applique à chaque site, sans répéter le travail établissement par établissement. Et vous décidez quelles notes reçoivent une réponse automatique et avec quel délai : par défaut, les 4 et 5 étoiles sont automatisées et les basses restent en alerte, ce qui est raisonnable, mais vous pouvez automatiser jusqu’à 1 étoile si vous préférez.',
      p2: 'Les mots qui comptent pour vous sont surveillés dans tout le groupe à la fois : dès qu’ils apparaissent dans un nouvel avis de n’importe lequel de vos établissements, l’e-mail vous parvient immédiatement. Et avec les alertes sur note basse, vous êtes au courant du mauvais avis en quelques minutes, quel que soit l’établissement.',
      bullets: [
        {
          clave: 'tono', t: 'Ton propre ou ton de marque',
          d: 'Chaque site avec sa voix, ou une seule voix partout si vous préférez la cohérence.',
        },
        {
          clave: 'palabras', t: 'Mots-clés partagés',
          d: 'Une règle générale qui surveille les avis de tous vos établissements, et les exceptions que vous voulez par site.',
        },
        {
          clave: 'alertas', t: 'Alertes immédiates',
          d: 'Vous réagissez vite où que soit le problème, sans regarder chaque établissement séparément.',
        },
      ],
      ahorra: 'Une configuration pour tout le groupe, pas une par site.',
    },
    {
      clave: 'publicaciones',
      captura: {
        ...SUGERENCIAS,
        alt: 'Les suggestions de l’IA pour une publication : trois textes proposés à partir de la photo, de la fiche Google et du site web, chacun avec son bouton « Utiliser ce texte ».',
      },
      kicker: 'Changements et publications en lot',
      titulo: 'Un changement, tout le groupe mis à jour',
      p1: 'Si un jour férié tombe ou si vous changez les horaires de saison, pas besoin d’entrer établissement par établissement. Vous choisissez les sites, vous le changez une fois et cela s’applique partout : horaires, catégories, téléphones, site web, réseaux sociaux et équipements. Les photos pareil, en envoyant un lot à plusieurs établissements d’un coup.',
      p2: 'Et les publications s’écrivent une seule fois avec des variables comme le nom de l’établissement, la ville ou l’adresse : elles sortent adaptées sur chaque site sans répéter le travail. Fini d’écrire la même offre vingt fois avec vingt adresses différentes.',
      bullets: [
        {
          clave: 'horarios', t: 'Horaires en lot',
          d: 'Jours fériés et saisons appliqués d’un coup aux sites que vous choisissez.',
        },
        {
          clave: 'variables', t: 'Variables par site',
          d: 'Le nom, la ville, l’adresse, le téléphone et le site web de chaque établissement se remplissent tout seuls.',
        },
        {
          clave: 'programacion', t: 'Programmation à l’avance',
          d: 'Le mois entier monté d’une traite, jusqu’à 180 jours à l’avance.',
        },
      ],
      ahorra: 'Vingt publications dans le temps d’en écrire une.',
    },
  ],

  informe: {
    captura: {
      ...INFORME,
      alt: 'Une page du rapport mensuel en PDF : le taux de réponse et le temps moyen face à leur valeur recommandée, et l’analyse de sentiment avec ce que les clients aiment et ce qu’ils amélioreraient.',
    },
    titulo: 'Un rapport d’ensemble, et le détail de chaque site si vous en avez besoin',
    p1: 'Le 1er de chaque mois, vous recevez un PDF avec les performances de tout votre groupe : avis, sentiment, mots-clés de vos clients et vos indicateurs de visibilité, en additionnant tous vos établissements. Et s’il vous faut le détail d’un site en particulier, vous avez son rapport individuel avec la même profondeur, sans avoir à l’additionner vous-même établissement par établissement.',
    p2: 'C’est le document que vous pouvez emmener en réunion de direction sans rien préparer : ce qui s’est passé dans tout le groupe le mois dernier, et sur quel site mettre l’accent le mois prochain.',
  },

  logos: 'Quelques-unes des marques qui font confiance à MyTapstar pour gagner des positions',

  faqTitulo: 'Questions fréquentes',
  faq: [
    {
      q: 'Dois-je connecter chaque établissement séparément ?',
      a: 'En général non, pas de zéro : si toutes vos fiches sont déjà dans le même compte Google Business, vous les connectez ensemble en reliant ce compte. Vous n’auriez à les connecter une par une que si elles sont réparties sur des comptes Google différents.',
    },
    {
      q: 'Les données d’un établissement se mélangent-elles avec celles d’un autre ?',
      a: 'Non. Chaque établissement garde ses propres avis, sa propre note et son propre scanner. La seule chose agrégée est ce que vous décidez de consulter : la couverture de marque au sein d’une analyse, ou le rapport mensuel de tout le groupe.',
    },
    {
      q: 'Puis-je avoir des établissements avec des tons de réponse différents dans le même groupe ?',
      a: 'Oui, chaque établissement peut avoir son propre ton, ou vous pouvez appliquer le même à tous si vous préférez une seule voix de marque.',
    },
    {
      q: 'Puis-je changer les horaires de tous mes sites d’un coup ?',
      a: 'Oui. Vous choisissez les sites, vous faites le changement une fois et il s’applique partout : horaires, catégories, téléphones, site web, réseaux sociaux, équipements et photos. Ce qui se fait encore fiche par fiche, ce sont les informations propres à chacune — la description, l’adresse — et les liens d’action. Le nom de l’établissement ne se change en aucun cas depuis ici : Google le soumet à une vérification et il faut le modifier dans leur tableau de bord.',
    },
    {
      q: 'Y a-t-il un minimum d’établissements pour passer par ce formulaire ?',
      a: 'Si vous avez moins de 5 établissements, mieux vaut commencer directement depuis la page d’accueil avec l’essai gratuit de 7 jours.',
    },
  ],
};

export const multiubicacionesV3 = { es, en, pt, de, fr };
